// @flow

import { getTileBBox as getMecartorBBox } from "@mapbox/whoots-js";
import EXTENT from "../data/extent";
import Point from "@mapbox/point-geometry";
import MercatorCoordinate from "../geo/mercator_coordinate";

import assert from "assert";
import { register } from "../util/web_worker_transfer";
import { TileUrlReplaceSpecification } from "../style-spec/types";

import CRS from "../geo/crs/crs";

export class CanonicalTileID {
    z: number;
    x: number;
    y: number;
    z_replace: string;
    y_replace: string;
    x_replace: string;
    key: string;

    constructor(z: number, x: number, y: number) {
        this.z = z;
        this.x = x;
        this.y = y;
        this.key = calculateKey(0, z, z, x, y);
        this.z_replace = undefined;
        this.x_replace = undefined;
        this.y_replace = undefined;
    }

    offsetZ(offset: number) {
        if (offset) {
            this.z += offset;
        }
    }

    replaceX(x: any) {
        this.x_replace = x;
    }

    replaceY(y: any) {
        this.y_replace = y;
    }

    replaceZ(z: any) {
        this.z_replace = z;
    }

    format(str, size) {
        //截取右8位，不足的位数前面补0
        var i = str.length;
        if (i >= size) {
            str = str.substring(i - size, i);
        } else {
            var str0 = "";
            for (let i = 0; i < size; i++) {
                str0 += "0";
            }
            str = str0.substring(0, str0.length - i) + str;
        }
        return str;
    }

    replaceRule(replace: TileUrlReplaceSpecification, value): string {
        const { type, fixlength, prefix, postfix } = replace;
        let str = value;
        let fix = 0;
        switch (type) {
            case "hex":
                str = value.toString(16).toUpperCase();
                break;
            case "dec":
                str = value.toString(10).toUpperCase();
                break;
            case "oct":
                str = value.toString(8).toUpperCase();
                break;
            case "bin":
                str = value.toString(2).toUpperCase();
                break;
            case "replace":
                break;
        }
        if (fixlength) {
            str = this.format(str, fixlength);
        }

        if (prefix) {
            str = prefix + str;
        }
        if (postfix) {
            str = str + postfix;
        }
        return str;
    }

    equals(id: CanonicalTileID) {
        return this.z === id.z && this.x === id.x && this.y === id.y;
    }

    // 获取bbox，该方法逻辑抽取自url()
    _getBBox(isReverse, crs) {
        let bbox = this.getTileBBox(this.x, this.y, this.z, crs);
        if (isReverse) {
            let bound = bbox.split(",");
            bbox = `${bound[1]},${bound[0]},${bound[3]},${bound[2]}`;
        }
        return bbox;
    }

    // given a list of urls, choose a url template and return a tile URL
    url(urls: Array<string>, scheme: ?string, crs?: CRS) {
        const { x_replace, y_replace, z_replace } = this;
        // mapbox-gl-修改说明：避免行列号相加为负数，导致无法获取url列表值
        // 修改人：赵凯 2024年9月19日
        const url = urls[Math.abs(this.x + this.y) % urls.length];
        const reverse = url.indexOf("reversebbox=true") >= 0 ? true : false;
        // mapbox-gl-修改说明：将crs继续往getTileBBox里传
        // 修改人：龚跃健 2024年6月19日
        let bbox = this.getTileBBox(this.x, this.y, this.z, crs);
        if (reverse) {
            let bound = bbox.split(",");
            bbox = `${bound[1]},${bound[0]},${bound[3]},${bound[2]}`;
            console.warn("reverse", bbox);
        }
        // const bbox3857 = getTileBBox(this.x, this.y, this.z);
        // const bbox4326 = getLatlngBBox(this.x, this.y, this.z);
        const quadkey = getQuadkey(this.z, this.x, this.y);
        let z_rule = this.z,
            x_rule = this.x,
            y_rule = this.y;

        if (x_replace || y_replace || z_replace) {
            if (z_replace) {
                z_rule = this.replaceRule(z_replace, this.z);
            }
            if (x_replace) {
                x_rule = this.replaceRule(x_replace, this.x);
            }
            if (y_replace) {
                y_rule = this.replaceRule(y_replace, this.y);
            }
        }

        return url
            .replace(
                "{prefix}",
                (this.x % 16).toString(16) + (this.y % 16).toString(16)
            )
            .replace("{z}", String(z_rule))
            .replace("{x}", String(x_rule))
            .replace(
                "{y}",
                String(
                    scheme === "tms" ? -this.y - 1 : y_rule
                )
            )
            .replace("{quadkey}", quadkey)
            .replace("{bbox}", bbox);
    }


    getTilePoint(coord: MercatorCoordinate) {
        const tilesAtZoom = Math.pow(2, this.z);
        return new Point(
            (coord.x * tilesAtZoom - this.x) * EXTENT,
            (coord.y * tilesAtZoom - this.y) * EXTENT
        );
    }

    getCRS() {
        return CRS.EPSG3857;
    }

    toString() {
        return `${this.z}/${this.x}/${this.y}`;
    }

    // mapbox-gl-修改说明：getTileBBox方法重构，支持自定义坐标系
    // 修改人：赵凯 2023年8月11日
    // mapbox-gl-修改说明：将方法从map.js中移到tile_id.js中
    // 修改人：龚跃健 2024年6月18日
    getTileBBox(x, y, z, crs) {
        if (crs) {
            const { width, height, originX, originY } = crs;
            // scale是计算每张瓦片对应的实际地图单位 360/2^n
            const tileSize = crs.tileSize;
            let scale;
            if (tileSize < 0) {
                scale =
                    Math.max(width, height) / Math.pow(2, this.z);
            } else {
                scale =
                    crs.getResolution(this.z, tileSize) * tileSize;
            }
            // 计算瓦片的左边界，单位为参考系单位
            const left = scale * x + originX;
            // 计算瓦片的右边界，单位为参考系单位
            const right = scale * (x + 1) + originX;
            // 计算瓦片的上边界，单位为参考系单位
            const top = originY - scale * y;
            // 计算瓦片的下边界，单位为参考系单位
            const bottom = originY - scale * (y + 1);
            const bbox = `${left},${bottom},${right},${top}`;
            return bbox;
        } else {
            return getMecartorBBox(x, y, z);
        }
    }
}
// mapbox-gl-修改说明：添加scaleFactors和sourceId属性，用以描述各层级比例因子和sourceId
// 修改人：赵凯 2024年4月15日
export class UnwrappedTileID {
    wrap: number;
    canonical: CanonicalTileID;
    key: string;

    constructor(wrap: number, canonical: CanonicalTileID) {
        this.wrap = wrap;
        this.canonical = canonical;
        this.key = calculateKey(
            wrap,
            canonical.z,
            canonical.z,
            canonical.x,
            canonical.y
        );
    }
}

export class OverscaledTileID {
    overscaledZ: number;
    wrap: number;
    canonical: CanonicalTileID;
    key: string;
    posMatrix: Float32Array;

    constructor(overscaledZ: number, wrap: number, z: number, x: number, y: number) {
        assert(overscaledZ >= z);
        this.overscaledZ = overscaledZ;
        this.wrap = wrap;
        this.canonical = new CanonicalTileID(z, +x, +y);
        this.key = calculateKey(wrap, overscaledZ, z, x, y);
    }

    equals(id: OverscaledTileID) {
        return this.overscaledZ === id.overscaledZ && this.wrap === id.wrap && this.canonical.equals(id.canonical);
    }

    scaledTo(targetZ: number) {
        assert(targetZ <= this.overscaledZ);
        const zDifference = this.canonical.z - targetZ;
        if (targetZ > this.canonical.z) {
            return new OverscaledTileID(targetZ, this.wrap, this.canonical.z, this.canonical.x, this.canonical.y);
        } else {
            return new OverscaledTileID(targetZ, this.wrap, targetZ, this.canonical.x >> zDifference, this.canonical.y >> zDifference);
        }
    }

    /*
     * calculateScaledKey is an optimization:
     * when withWrap == true, implements the same as this.scaledTo(z).key,
     * when withWrap == false, implements the same as this.scaledTo(z).wrapped().key.
     */
    calculateScaledKey(targetZ: number, withWrap: boolean): string {
        assert(targetZ <= this.overscaledZ);
        const zDifference = this.canonical.z - targetZ;
        if (targetZ > this.canonical.z) {
            return calculateKey(this.wrap * +withWrap, targetZ, this.canonical.z, this.canonical.x, this.canonical.y);
        } else {
            return calculateKey(this.wrap * +withWrap, targetZ, targetZ, this.canonical.x >> zDifference, this.canonical.y >> zDifference);
        }
    }

    isChildOf(parent: OverscaledTileID) {
        if (parent.wrap !== this.wrap) {
            // We can't be a child if we're in a different world copy
            return false;
        }
        const zDifference = this.canonical.z - parent.canonical.z;
        // We're first testing for z == 0, to avoid a 32 bit shift, which is undefined.
        return parent.overscaledZ === 0 || (
            parent.overscaledZ < this.overscaledZ &&
                parent.canonical.x === (this.canonical.x >> zDifference) &&
                parent.canonical.y === (this.canonical.y >> zDifference));
    }

    children(sourceMaxZoom: number) {
        if (this.overscaledZ >= sourceMaxZoom) {
            // return a single tile coord representing a an overscaled tile
            return [new OverscaledTileID(this.overscaledZ + 1, this.wrap, this.canonical.z, this.canonical.x, this.canonical.y)];
        }

        const z = this.canonical.z + 1;
        const x = this.canonical.x * 2;
        const y = this.canonical.y * 2;
        return [
            new OverscaledTileID(z, this.wrap, z, x, y),
            new OverscaledTileID(z, this.wrap, z, x + 1, y),
            new OverscaledTileID(z, this.wrap, z, x, y + 1),
            new OverscaledTileID(z, this.wrap, z, x + 1, y + 1)
        ];
    }

    isLessThan(rhs: OverscaledTileID) {
        if (this.wrap < rhs.wrap) return true;
        if (this.wrap > rhs.wrap) return false;

        if (this.overscaledZ < rhs.overscaledZ) return true;
        if (this.overscaledZ > rhs.overscaledZ) return false;

        if (this.canonical.x < rhs.canonical.x) return true;
        if (this.canonical.x > rhs.canonical.x) return false;

        if (this.canonical.y < rhs.canonical.y) return true;
        return false;
    }

    wrapped() {
        return new OverscaledTileID(this.overscaledZ, 0, this.canonical.z, this.canonical.x, this.canonical.y);
    }

    unwrapTo(wrap: number) {
        return new OverscaledTileID(this.overscaledZ, wrap, this.canonical.z, this.canonical.x, this.canonical.y);
    }

    overscaleFactor() {
        return Math.pow(2, this.overscaledZ - this.canonical.z);
    }

    toUnwrapped() {
        return new UnwrappedTileID(this.wrap, this.canonical);
    }

    toString() {
        return `${this.overscaledZ}/${this.canonical.x}/${this.canonical.y}`;
    }

    getTilePoint(coord: MercatorCoordinate) {
        return this.canonical.getTilePoint(new MercatorCoordinate(coord.x - this.wrap, coord.y));
    }
}

function calculateKey(wrap: number, overscaledZ: number, z: number, x: number, y: number): string {
    wrap *= 2;
    if (wrap < 0) wrap = wrap * -1 - 1;
    const dim = 1 << z;
    // mapbox-gl-修改说明：支持负数瓦片和非0级多张瓦片的逻辑后，原来的编码方式需要修改，保证瓦片key值的唯一
    // 修改人：赵凯 2024年9月5日
    return (
        (dim * dim * wrap + dim * y + x).toString(36) +
        x.toString(36) +
        y.toString(36) +
        z.toString(36) +
        overscaledZ.toString(36)
    );
}

function getQuadkey(z, x, y) {
    let quadkey = '', mask;
    for (let i = z; i > 0; i--) {
        mask = 1 << (i - 1);
        quadkey += ((x & mask ? 1 : 0) + (y & mask ? 2 : 0));
    }
    return quadkey;
}

register('CanonicalTileID', CanonicalTileID);
register('OverscaledTileID', OverscaledTileID, {omit: ['posMatrix']});
